import type { OrcamentoApp, ItemOrcamentoApp } from './api';

// O PDF do orcamento do PRESTADOR.
//
// ⚠️ **O documento e' DELE, nao nosso — e e' isso que o torna vendavel.** O nome do
// prestador vai no topo, em corpo grande; "Fio de Cobre" aparece so num rodape
// discreto, como procedencia do material. ⛔ **Nao por a marca da loja como
// emissora** (logo, endereco, CNPJ, "orcamento Fio de Cobre"): quem entrega esse
// papel ao cliente final e' ele, e um papel que se anuncia como nosso o transforma
// em entregador de orcamento da loja. Decisao do Ruan, 17/08/2026.
//
// ⛔ **Nao reusar o `utils/gerarPdfOrcamento.ts` do ERP.** Aquele e' o orcamento da
// LOJA: logo, banner promocional, numero de pedido, vendedor. Reusa-lo aqui traria
// tudo o que este documento nao pode ter.
//
// ⚠️ O `jspdf` entra por **import dinamico**: o AppCel roda em celular, e a
// biblioteca so faz sentido no toque em "Compartilhar" — carrega-la na abertura do
// aplicativo custaria o download a todo mundo que nunca vai gerar um papel.

const COBRE: [number, number, number] = [180, 83, 9];
const CINZA: [number, number, number] = [110, 110, 110];
const PRETO: [number, number, number] = [17, 17, 17];
const LINHA: [number, number, number] = [214, 211, 209];

/** Os sinais combinantes do Unicode (U+0300..U+036F), que o `normalize('NFD')`
 *  separa da letra. Montado por escape, e nao escrito a mao: no fonte, o proprio
 *  caractere nao aparece. */
const RE_ACENTO = new RegExp(`[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`, 'g');

const MARGEM = 14;
const LARGURA = 210;
const FIM_PAGINA = 276;

/** Lado da foto no papel, em mm. */
const FOTO_MM = 13;
/** Quanto o texto recua quando ha foto (o quadrado + o respiro). */
const RECUO_FOTO = FOTO_MM + 3.5;
/** Lado do quadrado que vai DENTRO do PDF, em pixels. 13mm impressos a ~300dpi dao
 *  ~154px; acima disso so engorda o arquivo que ele manda pelo WhatsApp. */
const FOTO_PX = 160;
/** Teto pra buscar as fotos. Estourou, o papel sai SEM elas — atrasar o
 *  compartilhamento e' pior que um orcamento so de texto, que ja era o de ontem. */
const TETO_FOTOS_MS = 8000;

/**
 * Baixa as fotos e devolve cada uma como JPEG pequeno, pronto pro `addImage`.
 *
 * ⚠️ **Passa por `<canvas>` de proposito, e nao por dois motivos — por tres**: (a) a
 * derivada do catalogo chega com ate 1200x1200 pra ocupar 13mm, e mandar o arquivo
 * cheu pro PDF faria um documento de dezenas de MB pra ele compartilhar no celular;
 * (b) parte do acervo e' `.webp`, que o jsPDF nao sabe embutir; (c) o quadrado com
 * **fundo branco** e a sobra em volta reproduzem no papel o mesmo `contain` que a
 * tela usa ([DA-526]) — sem ele, foto de produto comprido sairia esticada.
 *
 * ⛔ Falha de uma foto **nao derruba o PDF**: aquela linha sai sem imagem, como as de
 * mao de obra. O documento e' o que ele entrega ao cliente dele — melhor sem foto do
 * que sem papel.
 */
async function carregarFotos(urls: string[]): Promise<Map<string, string>> {
  const mapa = new Map<string, string>();
  const unicas = [...new Set(urls)];
  if (!unicas.length) return mapa;

  const prazo = new Promise<void>((r) => setTimeout(r, TETO_FOTOS_MS));

  await Promise.race([prazo, Promise.all(unicas.map(async (url) => {
    try {
      const img = await new Promise<HTMLImageElement>((ok, falhou) => {
        const el = new Image();
        // Mesma origem (o `/uploads` e' servido pelo nosso nginx), entao o canvas
        // nao fica marcado e o `toDataURL` funciona.
        el.onload = () => ok(el);
        el.onerror = () => falhou(new Error('foto'));
        el.src = url;
      });
      const cv = document.createElement('canvas');
      cv.width = FOTO_PX;
      cv.height = FOTO_PX;
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, FOTO_PX, FOTO_PX);
      const escala = Math.min(FOTO_PX / img.naturalWidth, FOTO_PX / img.naturalHeight);
      const w = img.naturalWidth * escala;
      const h = img.naturalHeight * escala;
      ctx.drawImage(img, (FOTO_PX - w) / 2, (FOTO_PX - h) / 2, w, h);
      mapa.set(url, cv.toDataURL('image/jpeg', 0.82));
    } catch {
      /* uma foto a menos, papel na mesma */
    }
  }))]);

  return mapa;
}

/** Teto da logo no papel: 22mm de altura e 55mm de largura, o que vier primeiro. */
const LOGO_ALT_MM = 22;
const LOGO_LARG_MM = 55;

/**
 * A logo do prestador, medida pra caber no alto do papel **sem deformar**.
 *
 * ⚠️ Diferente da foto de produto, aqui NAO ha quadrado: logo e' larga na maioria
 * das vezes, e forcar 1:1 acrescentaria tarja branca ou esticaria a marca — que e' a
 * unica coisa da folha que nao pode sair torta. O `<canvas>` entra pelo mesmo motivo
 * de sempre (peso e `.webp` que o jsPDF nao embute).
 */
async function carregarLogo(url: string): Promise<{ dataUrl: string; larguraMm: number; alturaMm: number } | null> {
  try {
    const img = await new Promise<HTMLImageElement>((ok, falhou) => {
      const el = new Image();
      el.onload = () => ok(el);
      el.onerror = () => falhou(new Error('logo'));
      el.src = url;
    });
    const escala = Math.min(LOGO_LARG_MM / img.naturalWidth, LOGO_ALT_MM / img.naturalHeight);
    const larguraMm = img.naturalWidth * escala;
    const alturaMm = img.naturalHeight * escala;

    // ~300dpi no tamanho impresso: 1mm = 11.8px.
    const cv = document.createElement('canvas');
    cv.width = Math.max(1, Math.round(larguraMm * 11.8));
    cv.height = Math.max(1, Math.round(alturaMm * 11.8));
    const ctx = cv.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.drawImage(img, 0, 0, cv.width, cv.height);
    return { dataUrl: cv.toDataURL('image/jpeg', 0.9), larguraMm, alturaMm };
  } catch {
    // ⛔ Papel sem logo, nunca papel nenhum.
    return null;
  }
}

function moeda(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Quantidade sem casas inuteis: `3` e nao `3,000`; `2,5` continua `2,5`. */
function qtd(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function dataCurta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/** O valor que vale na linha: o digitado, ou a referencia viva quando nao ha. */
export function valorUnitarioEfetivo(i: ItemOrcamentoApp): number {
  return i.valor_unitario ?? i.preco_ref ?? 0;
}

export function totalDaLinha(i: ItemOrcamentoApp): number {
  return valorUnitarioEfetivo(i) * i.quantidade;
}

/**
 * Os tamanhos de foto que o papel tenta, do maior pro menor — 1 e' o quadrado de
 * 13mm de sempre, e o ultimo da' ~4mm, do tamanho de um icone.
 *
 * ⚠️ **A foto tem escada PROPRIA porque e' ela que manda na altura da linha**: 13mm
 * contra 4,6mm de texto, entao encolher a letra nao encurta nada enquanto o quadrado
 * nao ceder. Medido com fotos reais, numa folha: foto cheia aguenta ~14 linhas, pela
 * metade ~22, a 5mm ~27, a 4mm ~33.
 *
 * ⛔ **A foto nunca chega a ZERO** (decisao do Ruan, 20/08/2026, vendo os tres papeis
 * lado a lado): ele escolheu o desenho com a foto miuda em vez do que a removia. Um
 * orcamento sem foto nenhuma e' documento de outra especie, nao o mesmo apertado.
 */
const NIVEIS_FOTO = [1, 0.55, 0.4, 0.3];

/**
 * ⛔ O quanto o documento pode encolher, e o piso NAO e' negociavel a esmo: em 0,70 o
 * corpo do item ja cai de 10 pra 7pt, e um orcamento ilegivel e' pior que um de duas
 * folhas. Quem nao couber nem no ultimo nivel de foto volta a paginar **em tamanho
 * cheio e com a foto cheia** — ⛔ nunca comprimido E paginado, que seria o pior dos
 * dois.
 */
const K_MIN = 0.7;

/**
 * Monta o PDF e devolve um `File` pronto pro compartilhamento nativo.
 *
 * ⚠️ Devolve `File` (e nao Blob ou dataURL) porque e' o que o `navigator.share`
 * aceita — mesmo caminho ja provado no iPhone dele ([DA-524], [DA-558]).
 *
 * ⚠️ **O papel e' desenhado mais de uma vez de proposito** (ver `NIVEIS_FOTO` e
 * `K_MIN`): a conta de altura depende de quantas linhas cada descricao ocupa, e isso
 * so o proprio `splitTextToSize` responde — logo, medir e' desenhar. Como o caso
 * comum cabe de primeira, o preco (alguns milissegundos por tentativa, com as fotos
 * ja carregadas) so e' pago por quem estava indo pra segunda folha.
 */
export async function gerarPdfDoPrestador(doc: OrcamentoApp): Promise<File> {
  const { default: JsPDF } = await import('jspdf');

  // ---- Topo: a MARCA dele ------------------------------------------------
  //
  // ⚠️ **A fantasia vence o nome da pessoa** (migration 0237, pedido dele em
  // 18/08/2026). Quem entrega orcamento ao cliente final assina como empresa; o nome
  // proprio fica como queda quando ele nao configurou nada.
  const assinatura = (doc.fantasia ?? '').trim() || doc.prestador;
  const logo = doc.logo_url ? await carregarLogo(doc.logo_url) : null;

  const materiais = doc.itens.filter((i) => i.tipo === 'material');
  const servicos = doc.itens.filter((i) => i.tipo === 'servico');

  // As fotos do material, buscadas de uma vez so — fora do desenho, porque o papel
  // pode ser redesenhado em outra escala e baixar tudo de novo seria absurdo. No
  // caminho normal elas ja estao no cache do navegador: a tela do orcamento acabou
  // de exibir as mesmas.
  const fotos = await carregarFotos(
    materiais.map((i) => i.foto_url).filter((u): u is string => !!u),
  );

  /**
   * Desenha o documento inteiro na escala `k` (1 = tamanho cheio), com o quadrado da
   * foto multiplicado por `escalaFoto` — os valores em uso estao em `NIVEIS_FOTO`.
   * Zero e' aceito e some com a foto, mas ⛔ **nao entra na escada**: e' decisao do
   * Ruan que o papel nunca perca a imagem do produto.
   */
  function desenhar(k: number, escalaFoto: number) {
    /** Papel apertado poe o "2 x R$ 189,90" na MESMA linha do valor. */
    const detalheInline = escalaFoto < 1;
    const pdf = new JsPDF({ unit: 'mm', format: 'a4' });

    // ⚠️ A margem de cima NAO encolhe: 22mm e' o que separa o papel da borda, e
    // ganhar 6mm colando a logo no topo estragaria a folha inteira pra economizar
    // meia linha.
    let y = 22;

    function novaPaginaSePreciso(altura: number): void {
      if (y + altura <= FIM_PAGINA) return;
      pdf.addPage();
      y = 22;
    }

    pdf.setTextColor(...PRETO);
    pdf.setFont('helvetica', 'bold');

    if (logo) {
      // ⛔ Nao esticar pra um quadrado: logo achatada e' pior que logo pequena, e a
      // maioria e' larga.
      //
      // **Logo e nome na MESMA linha** (pedido dele em 18/08/2026, vendo o papel com a
      // logo real). O nome ocupa o que sobra a direita da imagem e desce ao centro
      // dela; se nao couber, o corpo encolhe ate 13 antes de quebrar em duas linhas —
      // ⛔ reduzir sem piso deixaria "FIODECOBRE INSTALACOES" ilegivel pra caber.
      const topo = y - 6 * k;
      const logoLarg = logo.larguraMm * k;
      const logoAlt = logo.alturaMm * k;
      pdf.addImage(logo.dataUrl, 'JPEG', MARGEM, topo, logoLarg, logoAlt);

      const x = MARGEM + logoLarg + 5 * k;
      const larguraTexto = LARGURA - MARGEM - x;
      let corpo = 20 * k;
      let linhas = pdf.setFontSize(corpo).splitTextToSize(assinatura.toUpperCase(), larguraTexto) as string[];
      while (linhas.length > 1 && corpo > 13 * k) {
        corpo -= 2 * k;
        linhas = pdf.setFontSize(corpo).splitTextToSize(assinatura.toUpperCase(), larguraTexto) as string[];
      }
      // 1pt = 0,3528mm; o 1,15 e' a entrelinha.
      const alturaLinha = corpo * 0.3528 * 1.15;
      const alturaBloco = linhas.length * alturaLinha;
      // ⚠️ O `y` do jsPDF e' a LINHA DE BASE: pra centralizar o bloco na altura da logo
      // e' preciso somar ~0,75 da linha (a parte da letra que fica ACIMA da base). A
      // versao anterior ignorava isso e o nome saiu POR CIMA da imagem em producao.
      const base = topo + logoAlt / 2 - alturaBloco / 2 + alturaLinha * 0.75;
      pdf.text(linhas, x, base);

      y = Math.max(topo + logoAlt, base + alturaBloco - alturaLinha * 0.75) + 6 * k;
    } else {
      pdf.setFontSize(20 * k);
      pdf.text(assinatura.toUpperCase(), MARGEM, y);
      y += 7 * k;
    }
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10 * k);
    pdf.setTextColor(...CINZA);
    pdf.text(`Orçamento · ${dataCurta(doc.atualizado_em || doc.criado_em)}`, MARGEM, y);

    y += 4 * k;
    pdf.setDrawColor(...COBRE);
    pdf.setLineWidth(0.8 * k);
    pdf.line(MARGEM, y, LARGURA - MARGEM, y);
    pdf.setLineWidth(0.2);

    y += 9 * k;

    // ---- Cliente dele ----------------------------------------------------
    const cliente = (doc.cliente_final ?? '').trim();
    if (cliente) {
      pdf.setFontSize(11 * k);
      pdf.setTextColor(...CINZA);
      pdf.text('Cliente', MARGEM, y);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...PRETO);
      pdf.setFontSize(12 * k);
      pdf.text(cliente, MARGEM + 20 * k, y);
      pdf.setFont('helvetica', 'normal');
      y += 9 * k;
    }

    // ---- Blocos ----------------------------------------------------------
    function tituloSecao(texto: string): void {
      novaPaginaSePreciso(14 * k);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9.5 * k);
      pdf.setTextColor(...COBRE);
      pdf.text(texto.toUpperCase(), MARGEM, y);
      y += 2.5 * k;
      pdf.setDrawColor(...LINHA);
      pdf.line(MARGEM, y, LARGURA - MARGEM, y);
      y += 5 * k;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...PRETO);
    }

    /**
     * Uma linha: numero, foto (quando ha) e descricao a esquerda, valores a direita.
     *
     * ⚠️ A altura da linha e' o MAIOR entre o texto e o quadrado da foto — senao a
     * imagem de um item de descricao curta invade a linha de baixo, e o `y` que
     * decide a quebra de pagina passa a mentir.
     *
     * ⚠️ **O numero mora numa COLUNA propria, alinhado a direita** (pedido dele em
     * 20/08/2026). Escrito colado na descricao (`1. CABO...`) ele empurraria o texto
     * de cada linha uma distancia diferente conforme o numero tivesse um, dois ou tres
     * digitos — e a lista inteira ficaria com a margem esquerda serrilhada.
     */
    function linhaItem(descricao: string, detalhe: string, valor: number, numero: number, foto?: string): void {
      pdf.setFontSize(10 * k);
      const temFoto = !!foto && escalaFoto > 0;
      const fotoMm = FOTO_MM * k * escalaFoto;
      // O respiro ao lado do quadrado nao encolhe junto com ele: 3,5mm e' o que separa
      // a imagem do texto, e uma foto menor nao precisa de menos separacao.
      const recuoFoto = fotoMm + 3.5 * k;
      // A coluna do numero cabe tres digitos: um orcamento nunca chegou perto disso,
      // mas o papel pagina, entao o teto nao e' o que cabe numa folha.
      const colunaNumero = 7 * k;
      const xTexto = MARGEM + colunaNumero + (temFoto ? recuoFoto : 0);
      // ⚠️ A COLUNA de texto nao encolhe junto: com a letra menor, a mesma descricao
      // passa a caber em menos linhas — parte do ganho da escala vem daqui.
      const larguraTexto = 118 - colunaNumero - (temFoto ? recuoFoto : 0);
      const partes = pdf.splitTextToSize(descricao, larguraTexto) as string[];
      const alturaTexto = Math.max(partes.length * 4.6 * k, (detalhe && !detalheInline ? 8.6 : 4.6) * k);
      const altura = Math.max(alturaTexto, temFoto ? fotoMm + 1 * k : 0);
      novaPaginaSePreciso(altura + 4 * k);

      // O numero e' referencia, nao conteudo: fica cinza e um corpo menor, pra nao
      // disputar leitura com a descricao do produto.
      pdf.setFontSize(8.5 * k);
      pdf.setTextColor(...CINZA);
      pdf.text(String(numero), MARGEM + colunaNumero - 2.5 * k, y, { align: 'right' });
      pdf.setFontSize(10 * k);

      if (temFoto) {
        // O `y` do jsPDF e' a LINHA DE BASE do texto; o quadrado sobe um pouco pra
        // ficar alinhado pelo topo da primeira linha, e nao pendurado abaixo dela.
        const topo = y - 3.4 * k;
        pdf.addImage(foto!, 'JPEG', MARGEM + colunaNumero, topo, fotoMm, fotoMm);
        pdf.setDrawColor(...LINHA);
        pdf.rect(MARGEM + colunaNumero, topo, fotoMm, fotoMm);
      }

      pdf.setTextColor(...PRETO);
      pdf.text(partes, xTexto, y);
      const rotuloValor = `R$ ${moeda(valor)}`;
      // ⚠️ Medido ANTES de trocar o corpo: o `getTextWidth` responde na fonte que
      // estiver valendo, e o detalhe e' desenhado menor que o valor.
      const larguraValor = pdf.getTextWidth(rotuloValor);
      pdf.text(rotuloValor, LARGURA - MARGEM, y, { align: 'right' });

      if (detalhe) {
        pdf.setFontSize(8.5 * k);
        pdf.setTextColor(...CINZA);
        if (detalheInline) {
          pdf.text(detalhe, LARGURA - MARGEM - larguraValor - 4 * k, y, { align: 'right' });
        } else {
          pdf.text(detalhe, LARGURA - MARGEM, y + 4.2 * k, { align: 'right' });
        }
      }

      y += altura + 2.6 * k;
    }

    function subtotal(rotulo: string, valor: number): void {
      novaPaginaSePreciso(10 * k);
      pdf.setDrawColor(...LINHA);
      pdf.line(MARGEM, y - 1 * k, LARGURA - MARGEM, y - 1 * k);
      y += 4 * k;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10 * k);
      pdf.setTextColor(...PRETO);
      pdf.text(rotulo, MARGEM, y);
      pdf.text(`R$ ${moeda(valor)}`, LARGURA - MARGEM, y, { align: 'right' });
      pdf.setFont('helvetica', 'normal');
      y += 10 * k;
    }

    // ⚠️ **A contagem corre pelo documento inteiro, nao por secao** (pedido dele em
    // 20/08/2026): duas listas reiniciando em 1 poriam dois "item 3" no mesmo papel, e
    // o numero existe justamente pra ele e o cliente apontarem uma linha sem ambiguidade.
    let numeroDaLinha = 0;

    if (materiais.length) {
      tituloSecao('Material');
      for (const i of materiais) {
        const unit = valorUnitarioEfetivo(i);
        // O detalhe so aparece quando ele diz alguma coisa: numa linha de 1 unidade
        // o "1 x R$ 50,00" repete o valor ao lado e so ocupa espaco.
        const detalhe = i.quantidade === 1 ? '' : `${qtd(i.quantidade)} x R$ ${moeda(unit)}`;
        // ⚠️ **MAIUSCULO so no MATERIAL, e so na hora de desenhar** (pedido dele em
        // 20/08/2026, pra igualar o ERP). Duas fontes alimentam esta descricao e so
        // uma e' caixa alta: a `descricao` do produto (a da NF e do caixa) vem sempre
        // maiuscula, mas o app prefere a `descricao_comercial`, escrita pra vitrine em
        // Caixa De Titulo. ⛔ **A mao de obra fica FORA**: e' frase que ele digita, com
        // corretor ligado e `no-upper` na tela desde que o campo nasceu — a mesma
        // regra das Observacoes. E o texto segue intacto no banco nos dois casos.
        linhaItem(i.descricao.toUpperCase(), detalhe, totalDaLinha(i), ++numeroDaLinha, i.foto_url ? fotos.get(i.foto_url) : undefined);
      }
      subtotal('Total do material', doc.totais.material);
    }

    if (servicos.length) {
      tituloSecao('Mão de obra');
      for (const i of servicos) {
        linhaItem(i.descricao, '', totalDaLinha(i), ++numeroDaLinha);
      }
      subtotal('Total da mão de obra', doc.totais.servico);
    }

    // ---- Total geral -----------------------------------------------------
    novaPaginaSePreciso(20 * k);
    pdf.setFillColor(245, 243, 241);
    pdf.rect(MARGEM, y - 5 * k, LARGURA - MARGEM * 2, 14 * k, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13 * k);
    pdf.setTextColor(...PRETO);
    pdf.text('TOTAL', MARGEM + 4 * k, y + 4 * k);
    pdf.setFontSize(15 * k);
    pdf.text(`R$ ${moeda(doc.totais.total)}`, LARGURA - MARGEM - 4 * k, y + 4 * k, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    y += 20 * k;

    // ---- Observacoes -----------------------------------------------------
    const obs = (doc.observacoes ?? '').trim();
    if (obs) {
      pdf.setFontSize(9.5 * k);
      pdf.setTextColor(...COBRE);
      pdf.setFont('helvetica', 'bold');
      novaPaginaSePreciso(12 * k);
      pdf.text('OBSERVAÇÕES', MARGEM, y);
      y += 5 * k;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10 * k);
      pdf.setTextColor(...PRETO);
      const linhas = pdf.splitTextToSize(obs, LARGURA - MARGEM * 2) as string[];
      novaPaginaSePreciso(linhas.length * 4.6 * k);
      pdf.text(linhas, MARGEM, y);
      y += linhas.length * 4.6 * k;
    }

    // ---- Rodape discreto, em TODAS as paginas ----------------------------
    // ⚠️ Rodape por pagina, e nao so na ultima: um PDF de duas folhas impresso e
    // entregue solto perderia a procedencia na folha que faltasse. ⛔ Ele NAO
    // encolhe com a escala: mora colado na borda de baixo, onde nao disputa espaco
    // com nada, e 8pt ja e' o menor corpo do papel.
    const total = pdf.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      pdf.setPage(p);
      pdf.setFontSize(8);
      pdf.setTextColor(...CINZA);
      pdf.text('Material: Fio de Cobre', MARGEM, 288);
      if (total > 1) {
        pdf.text(`${p}/${total}`, LARGURA - MARGEM, 288, { align: 'right' });
      }
    }

    return pdf;
  }

  const cabe = (p: { getNumberOfPages(): number }) => p.getNumberOfPages() === 1;

  // O papel cheio e' a primeira tentativa E a queda de quem nao couber em lugar
  // nenhum — quem ja cabia sai exatamente igual e nem paga o segundo desenho.
  let pdf = desenhar(1, 1);

  // ⚠️ **Procura o MAIOR papel que ainda cabe, nao o primeiro que couber.** A foto
  // cede por niveis; dentro de cada nivel, a escala e' achada por busca binaria,
  // senao um documento que caberia a 92% sairia a 70% so porque o degrau seguinte da
  // lista era esse — apertar mais que o necessario e' perda de legibilidade de graca.
  //
  // O custo fica baixo porque cada nivel e' **descartado com um unico desenho**: se
  // nem no papel mais apertado (`K_MIN`) ele couber, nao ha o que buscar ali dentro.
  if (!cabe(pdf)) {
    for (const foto of NIVEIS_FOTO) {
      const oMaisApertado = desenhar(K_MIN, foto);
      if (!cabe(oMaisApertado)) continue;

      let piso = K_MIN;
      let teto = 1;
      let melhor = oMaisApertado;
      for (let i = 0; i < 4; i++) {
        const meio = (piso + teto) / 2;
        const tentativa = desenhar(meio, foto);
        if (cabe(tentativa)) {
          melhor = tentativa;
          piso = meio;
        } else {
          teto = meio;
        }
      }
      pdf = melhor;
      break;
    }
  }

  const nome = nomeDoArquivo(doc);
  return new File([pdf.output('blob')], nome, { type: 'application/pdf' });
}

/**
 * O nome que aparece na tela de compartilhar e chega em quem recebe.
 *
 * ⚠️ Quem abre isto e' o CLIENTE DELE, que nunca ouviu falar do nosso sistema —
 * `orcamento-847.pdf` nao diz nada. Sai `orcamento-JOAO-17-08.pdf`. Mesma licao do
 * nome de arquivo da [DA-524] p.9.
 */
function nomeDoArquivo(doc: OrcamentoApp): string {
  const cliente = (doc.cliente_final ?? '')
    // Acento fora do nome do arquivo: NFD separa a letra do sinal, e a faixa de
    // combinantes apaga so o sinal. ⚠️ A faixa vai por ESCAPE (`RE_ACENTO`, no topo
    // do arquivo) — escrita literal no fonte ela e' invisivel pra quem vier ler.
    .normalize('NFD').replace(RE_ACENTO, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 28);
  const d = new Date(doc.atualizado_em || doc.criado_em);
  const carimbo = Number.isNaN(d.getTime())
    ? ''
    : `-${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  return `orcamento${cliente ? `-${cliente}` : ''}${carimbo}.pdf`;
}
